import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdRevenueAdType,
  AdRevenueReportRecordRow,
  AdRevenueReportStatus,
} from "../../shared/types/AdRevenueManagement";
import { getAdRevenueReportRecords, retryAdRevenueReports } from "./adRevenueAdminApi";
import { FilterBar, JsonBlock, Section, SectionHeader } from "./commercialAnalyticsStyles";
import { formatDateTime, formatInteger } from "./commercialAnalyticsUtils";

interface ReportFilters {
  range: [Dayjs, Dayjs];
  statuses?: AdRevenueReportStatus[];
  adType?: AdRevenueAdType;
  adUnitId?: string;
  adPlacementName?: string;
  userId?: string;
  providerCode?: string;
}

const DEFAULT_FILTERS: ReportFilters = {
  range: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")],
};

const STATUS_OPTIONS: Array<{ label: string; value: AdRevenueReportStatus }> = [
  { label: "等待配置", value: "waiting_config" },
  { label: "待上报", value: "pending" },
  { label: "仅采集", value: "captured" },
  { label: "上报中", value: "reporting" },
  { label: "上报成功", value: "succeeded" },
  { label: "上报失败", value: "failed" },
  { label: "已跳过", value: "skipped" },
];

export default function TencentAdRevenueReportsView() {
  const [draft, setDraft] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [rows, setRows] = useState<AdRevenueReportRecordRow[]>([]);
  const [total, setTotal] = useState(0);
  const [accessibleTotal, setAccessibleTotal] = useState(0);
  const [maxAccessibleRecords, setMaxAccessibleRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [detail, setDetail] = useState<AdRevenueReportRecordRow>();
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const data = await getAdRevenueReportRecords({
        page,
        pageSize,
        startTime: filters.range[0].valueOf(),
        endTime: filters.range[1].valueOf(),
        statuses: filters.statuses,
        adType: filters.adType,
        adUnitId: normalizeOptionalFilter(filters.adUnitId),
        adPlacementName: normalizeOptionalFilter(filters.adPlacementName),
        userId: normalizeOptionalFilter(filters.userId),
        providerCode: normalizeOptionalFilter(filters.providerCode),
        sortOrder: "desc",
      });
      if (sequence !== loadSequence.current) return;
      setRows(data.rows);
      setTotal(data.total);
      setAccessibleTotal(data.accessibleTotal);
      setMaxAccessibleRecords(data.maxAccessibleRecords);
      if (data.page !== page) setPage(data.page);
      const retryableIds = new Set(data.rows.filter((row) => row.retryable).map((row) => row.id));
      setSelectedIds((current) => current.filter((id) => retryableIds.has(String(id))));
    } catch (error) {
      if (sequence === loadSequence.current) {
        message.error(readErrorMessage(error, "腾讯变现上报明细加载失败"));
      }
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void load();
    return () => { loadSequence.current += 1; };
  }, [load]);

  const applyFilters = () => {
    setPage(1);
    setFilters({ ...draft });
  };

  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  };

  const requestRetry = (reportIds: string[]) => {
    if (!reportIds.length) return;
    const promotesCaptured = rows.some((row) => reportIds.includes(row.id) && row.status === "captured");
    Modal.confirm({
      title: reportIds.length === 1 ? "确认重试这条上报任务？" : `确认重试 ${reportIds.length} 条上报任务？`,
      content: promotesCaptured
        ? "所选任务包含“仅采集”记录。确认后会将其显式晋升为待上报；仅当游戏服处于 live 且全部安全门禁通过时才会发送。"
        : "服务端会重新校验任务状态和幂等键；已经成功或不可重试的任务不会重复发送。",
      okText: "提交重试",
      cancelText: "取消",
      onOk: async () => {
        setRetrying(true);
        try {
          const data = await retryAdRevenueReports({ reportIds, reason: "管理后台手动重试" });
          if (data.skippedCount > 0) {
            message.warning(`已提交 ${data.retriedCount} 条，跳过 ${data.skippedCount} 条不可重试任务`);
          } else {
            message.success(`已提交 ${data.retriedCount} 条任务重试`);
          }
          setSelectedIds([]);
          await load();
        } catch (error) {
          message.error(readErrorMessage(error, "重试任务提交失败"));
        } finally {
          setRetrying(false);
        }
      },
    });
  };

  const selectedReportIds = useMemo(() => selectedIds.map(String), [selectedIds]);

  return (
    <>
      <FilterBar>
        <DatePicker.RangePicker
          showTime
          allowClear={false}
          value={draft.range}
          style={{ width: 330 }}
          onChange={(value) => value && setDraft((current) => ({ ...current, range: value as [Dayjs, Dayjs] }))}
        />
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          placeholder="上报状态"
          value={draft.statuses}
          options={STATUS_OPTIONS}
          style={{ width: 190 }}
          onChange={(statuses) => setDraft((current) => ({ ...current, statuses }))}
        />
        <Select
          allowClear
          placeholder="广告类型"
          value={draft.adType}
          style={{ width: 130 }}
          options={[{ label: "激励视频", value: "rewardedVideo" }, { label: "插屏广告", value: "interstitial" }]}
          onChange={(adType) => setDraft((current) => ({ ...current, adType }))}
        />
        <Input allowClear placeholder="广告位名称" value={draft.adPlacementName} style={{ width: 145 }} onChange={(event) => setDraft((current) => ({ ...current, adPlacementName: event.target.value }))} />
        <Input allowClear placeholder="广告位 ID" value={draft.adUnitId} style={{ width: 170 }} onChange={(event) => setDraft((current) => ({ ...current, adUnitId: event.target.value }))} />
        <Input allowClear placeholder="玩家 ID" value={draft.userId} style={{ width: 170 }} onChange={(event) => setDraft((current) => ({ ...current, userId: event.target.value }))} />
        <Input allowClear placeholder="腾讯状态码" value={draft.providerCode} style={{ width: 120 }} onChange={(event) => setDraft((current) => ({ ...current, providerCode: event.target.value }))} />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
        <Button onClick={resetFilters}>重置</Button>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>刷新</Button>
      </FilterBar>

      <Section>
        <SectionHeader>
          <Space wrap>
            <span>腾讯变现上报明细</span>
            <Typography.Text type="secondary">共 {formatInteger(total)} 条</Typography.Text>
            {total > accessibleTotal && (
              <Typography.Text type="warning">
                仅支持浏览前 {formatInteger(maxAccessibleRecords)} 条，请缩小筛选范围查看更多
              </Typography.Text>
            )}
          </Space>
          <Button
            type="primary"
            disabled={!selectedReportIds.length}
            loading={retrying}
            onClick={() => requestRetry(selectedReportIds)}
          >
            批量重试{selectedReportIds.length ? `（${selectedReportIds.length}）` : ""}
          </Button>
        </SectionHeader>
        <Table<AdRevenueReportRecordRow>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={rows}
          scroll={{ x: 1800 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            preserveSelectedRowKeys: true,
            onChange: setSelectedIds,
            getCheckboxProps: (row) => ({ disabled: !row.retryable }),
          }}
          pagination={{
            current: page,
            pageSize,
            total: accessibleTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (value) => `共 ${formatInteger(value)} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          columns={[
            { title: "创建时间", dataIndex: "createdAt", width: 170, render: formatDateTime },
            { title: "广告类型", dataIndex: "adType", width: 112, render: renderAdType },
            { title: "广告位名称", dataIndex: "adPlacementName", width: 150, ellipsis: true, render: emptyText },
            { title: "广告位 ID", dataIndex: "adUnitId", width: 175, ellipsis: true, render: emptyText },
            { title: "收益", dataIndex: "revenueCent", width: 95, render: renderRevenue },
            {
              title: "价格来源",
              width: 125,
              render: (_, row) => row.rateSource === "ad_unit"
                ? <Tag color="blue">广告位 v{row.rateRevision ?? "-"}</Tag>
                : row.rateSource === "ad_type"
                  ? <Tag color="purple">类型兜底 v{row.rateRevision ?? "-"}</Tag>
                  : <Tag>未配置</Tag>,
            },
            { title: "状态", dataIndex: "status", width: 105, render: renderStatus },
            { title: "尝试", dataIndex: "attempts", width: 70, render: formatInteger },
            { title: "腾讯状态码", dataIndex: "providerCode", width: 105, render: emptyText },
            {
              title: "结果信息",
              width: 220,
              ellipsis: true,
              render: (_, row) => (
                <Tooltip title={row.providerMessage || row.lastError || row.skipReason || "-"}>
                  <span>{row.providerMessage || row.lastError || row.skipReason || "-"}</span>
                </Tooltip>
              ),
            },
            {
              title: "trace_id",
              dataIndex: "providerTraceId",
              width: 175,
              ellipsis: true,
              render: (value) => value ? <Typography.Text copyable={{ text: String(value) }}>{String(value)}</Typography.Text> : "-",
            },
            { title: "最近尝试", dataIndex: "lastAttemptAt", width: 170, render: formatDateTime },
            { title: "下次重试", dataIndex: "nextRetryAt", width: 170, render: formatDateTime },
            {
              title: "操作",
              fixed: "right",
              width: 130,
              render: (_, row) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => setDetail(row)}>详情</Button>
                  <Button type="link" size="small" disabled={!row.retryable} onClick={() => requestRetry([row.id])}>重试</Button>
                </Space>
              ),
            },
          ]}
        />
      </Section>

      <Drawer
        title="腾讯变现上报详情"
        width="min(680px, 94vw)"
        open={!!detail}
        destroyOnHidden
        onClose={() => setDetail(undefined)}
      >
        <Typography.Paragraph type="secondary">
          以下数据由管理服务端脱敏返回，不包含 access token、OpenID 或原始 click_id。
        </Typography.Paragraph>
        <JsonBlock>{JSON.stringify(detail, null, 2)}</JsonBlock>
      </Drawer>
    </>
  );
}

function renderAdType(value: string) {
  if (value === "rewardedVideo") return "激励视频";
  if (value === "interstitial") return "插屏广告";
  return value || "-";
}

function renderRevenue(value: unknown) {
  const cent = Number(value);
  return Number.isFinite(cent) ? `¥${(cent / 100).toFixed(2)}` : "-";
}

function renderStatus(value: string) {
  const map: Record<string, { text: string; color?: string }> = {
    waiting_config: { text: "等待配置", color: "warning" },
    pending: { text: "待上报", color: "processing" },
    captured: { text: "仅采集", color: "cyan" },
    reporting: { text: "上报中", color: "blue" },
    succeeded: { text: "成功", color: "success" },
    failed: { text: "失败", color: "error" },
    skipped: { text: "跳过" },
  };
  const item = map[value] || { text: value || "-" };
  return <Tag color={item.color}>{item.text}</Tag>;
}

function emptyText(value: unknown) {
  return value == null || value === "" ? "-" : String(value);
}

function normalizeOptionalFilter(value?: string) {
  return value?.trim() || undefined;
}

function readErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}
